/**
 * DatePicker memoizzato
 * @module flyers/components/fields/MemoizedDateField
 */

import React from "react";
import { DatePicker } from "antd";
import FloatingField from "@shared/components/FloatingField.jsx";
import { parseDateValue } from "../../utils/formatters.js";

/**
 * Campo data ottimizzato con parsing intelligente
 */
const MemoizedDateField = React.memo(({ fieldKey, label, value, onChange, disabled, mode = "single" }) => {
    const isRange = mode === "range";
    const dateValue = React.useMemo(() => (value ? parseDateValue(value) : null), [value]);
    const rangeValue = React.useMemo(() => {
        if (!value || typeof value !== "string") return null;
        const parts = value.split(/[_|]/).map((item) => item.trim()).filter(Boolean);
        if (parts.length < 2) return null;
        const start = parseDateValue(parts[0]);
        const end = parseDateValue(parts[1]);
        if (!start || !end) return null;
        return [start, end];
    }, [value]);

    const handleChange = React.useCallback((date) => {
        const formatted = date ? date.format("YYYY-MM-DD") : "";
        onChange(fieldKey, formatted);
    }, [fieldKey, onChange]);

    const handleRangeChange = React.useCallback((dates) => {
        if (!dates || !dates.length) {
            onChange(fieldKey, "");
            return;
        }
        const [start, end] = dates;
        const formattedStart = start ? start.format("YYYY-MM-DD") : "";
        const formattedEnd = end ? end.format("YYYY-MM-DD") : "";
        const formatted = formattedStart && formattedEnd ? `${formattedStart}_${formattedEnd}` : "";
        onChange(fieldKey, formatted);
    }, [fieldKey, onChange]);

    return (
        <FloatingField label={label}>
            {isRange ? (
                <DatePicker.RangePicker
                    allowClear
                    allowEmpty={[true, true]}
                    format="DD/MM/YYYY"
                    value={rangeValue}
                    onChange={handleRangeChange}
                    style={{ width: "100%" }}
                    disabled={disabled}
                />
            ) : (
                <DatePicker
                    allowClear
                    allowEmpty
                    format="DD/MM/YYYY"
                    value={dateValue}
                    onChange={handleChange}
                    style={{ width: "100%" }}
                    disabled={disabled}
                />
            )}
        </FloatingField>
    );
}, (prevProps, nextProps) => {
    return (
        prevProps.value === nextProps.value &&
        prevProps.disabled === nextProps.disabled &&
        prevProps.fieldKey === nextProps.fieldKey &&
        prevProps.mode === nextProps.mode
    );
});

MemoizedDateField.displayName = "MemoizedDateField";

export default MemoizedDateField;
